import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import styles from "./Sidebar.module.css";

import { GripIcon, SettingsIcon } from "@/components/icons";
import { sectionCode, useNav } from "@/features/manual/api";
import { buildPageTree, hasReadablePage, moveWithinSiblings } from "@/features/manual/navTree";
import { AccessPopover } from "@/features/section-access/AccessPopover";
import {
  mergePages,
  mergeSections,
  type ManagedPage,
  type ManagedSection,
} from "@/features/section-access/managedTree";
import { RowActions } from "@/features/section-access/RowActions";
import { useManagedNav, type ManagedNav } from "@/features/section-access/useManagedNav";
import { useCurrentUser } from "@/hooks/useCurrentUser";

type PopoverClose = "escape" | "outside" | "toggle";

/**
 * The OPERATIONS MANUAL tree in the sidebar. For everyone it is plain
 * navigation; for cms.manage holders every row also carries hover-revealed
 * manage controls — a drag grip to reorder (sections, and pages within
 * their section) and a button opening the who-can-see-this popover. The
 * managed tree merges the caller's nav with the complete admin section
 * list, so reorder always sends the full id set and a section an admin has
 * restricted away from themselves stays visible here (dimmed) to manage back.
 * A section with no published page the reader can read is left out, except
 * for cms.manage holders, for whom this tree is the section management screen.
 */
export function SidebarTree() {
  const { sections: navSections, error } = useNav();
  const location = useLocation();
  const { permissions, csrfToken } = useCurrentUser();
  const canManage = permissions.includes("cms.manage");
  const admin = useManagedNav(canManage, csrfToken);
  const manageReady = canManage && admin.sections !== null && admin.roles.length > 0;

  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [popoverId, setPopoverId] = useState<string | null>(null);
  const popoverAnchorRef = useRef<HTMLButtonElement | null>(null);

  // The section owning the current route stays open; clicking another section
  // opens it (and navigates to its landing page via the Link).
  const routeSection = location.pathname.startsWith("/manual/")
    ? (location.pathname.split("/")[2] ?? null)
    : null;
  const effectiveOpen = openSlug ?? routeSection;

  const merged = useMemo(
    () => mergeSections(navSections, canManage ? admin.sections : null),
    [navSections, canManage, admin.sections]
  );
  const shown = canManage ? merged : (merged?.filter(hasReadablePage) ?? null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const announcements = useMemo(() => {
    const titles = new Map<string, string>();
    for (const s of merged ?? []) titles.set(s.id, s.title);
    return makeAnnouncements(titles);
  }, [merged]);

  function togglePopover(e: MouseEvent<HTMLButtonElement>, ownerId: string) {
    if (popoverId === ownerId) {
      setPopoverId(null);
      return;
    }
    popoverAnchorRef.current = e.currentTarget;
    setPopoverId(ownerId);
  }

  function closePopover(reason: PopoverClose) {
    if (reason === "escape") popoverAnchorRef.current?.focus();
    setPopoverId(null);
  }

  function onSectionDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id || !merged) return;
    const ids = merged.map((s) => s.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    void admin.reorderSections(arrayMove(ids, from, to));
  }

  if (error) return <div className={styles.navError}>Couldn&rsquo;t load OpsHub.</div>;
  if (!merged || !shown) return <div className={styles.navError}>Loading…</div>;

  return (
    <>
      {canManage && admin.opError && (
        <div className={styles.navError} role="alert">
          {admin.opError}
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        accessibility={{ announcements }}
        onDragEnd={onSectionDragEnd}
      >
        <SortableContext items={merged.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <ul className={styles.sections}>
            {shown.map((section) => (
              <SectionItem
                key={section.id}
                section={section}
                open={effectiveOpen === section.slug}
                manageReady={manageReady}
                admin={admin}
                activePath={location.pathname}
                popoverId={popoverId}
                popoverAnchorRef={popoverAnchorRef}
                onOpenSection={setOpenSlug}
                onTogglePopover={togglePopover}
                onClosePopover={closePopover}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {manageReady && <NewSectionRow admin={admin} />}
    </>
  );
}

/** Admin affordance at the foot of the tree: create a section in place. */
function NewSectionRow({ admin }: { admin: ManagedNav }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  const create = async () => {
    if (await admin.createSection(title)) {
      setTitle("");
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <div className={styles.newSectionRow}>
        <button type="button" className={styles.newSectionBtn} onClick={() => setOpen(true)}>
          + New section
        </button>
      </div>
    );
  }

  return (
    <div className={styles.newSectionBox}>
      <input
        type="text"
        value={title}
        placeholder="Section title"
        aria-label="Title for the new section"
        className={styles.newSectionInput}
        autoFocus
        disabled={admin.mutating}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void create();
          if (e.key === "Escape") setOpen(false);
        }}
      />
      <div className={styles.newSectionActions}>
        <button
          type="button"
          className={styles.newSectionAction}
          disabled={admin.mutating}
          onClick={() => void create()}
        >
          Create section
        </button>
        <button
          type="button"
          className={styles.newSectionAction}
          disabled={admin.mutating}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

interface SectionItemProps {
  section: ManagedSection;
  open: boolean;
  manageReady: boolean;
  admin: ManagedNav;
  activePath: string;
  popoverId: string | null;
  popoverAnchorRef: React.MutableRefObject<HTMLButtonElement | null>;
  onOpenSection: (slug: string) => void;
  onTogglePopover: (e: MouseEvent<HTMLButtonElement>, ownerId: string) => void;
  onClosePopover: (reason: PopoverClose) => void;
}

function SectionItem({
  section,
  open,
  manageReady,
  admin,
  activePath,
  popoverId,
  popoverAnchorRef,
  onOpenSection,
  onTogglePopover,
  onClosePopover,
}: SectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
    // Not manageable until this row's access data has loaded (roleIds known):
    // reordering a partial set 409s, and the popover would misread its roles.
    disabled: !manageReady || section.roleIds === null || admin.reordering,
  });
  const manageable = manageReady && section.roleIds !== null;

  const liClass = [styles.section, open ? styles.open : "", isDragging ? styles.dragging : ""]
    .filter(Boolean)
    .join(" ");

  const rowContent = (
    <>
      <span className={styles.sectionCode}>{sectionCode(section.ordinal)}</span>
      <span className={styles.sectionTitle}>{section.title}</span>
      {section.restricted && (
        <span className={styles.lock} title="Restricted section">
          ●
        </span>
      )}
    </>
  );

  return (
    <li
      ref={setNodeRef}
      className={liClass}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className={styles.rowWrap}>
        {section.readable ? (
          <Link
            to={`/manual/${section.slug}`}
            className={styles.sectionRow}
            onClick={() => onOpenSection(section.slug)}
          >
            {rowContent}
          </Link>
        ) : (
          <button
            type="button"
            className={`${styles.sectionRow} ${styles.rowDimmed}`}
            title="Your roles can't see this section — you can still manage it"
            aria-expanded={open}
            onClick={() => onOpenSection(section.slug)}
          >
            {rowContent}
          </button>
        )}
        {manageable && (
          <span className={styles.rowControls}>
            <button
              type="button"
              ref={setActivatorNodeRef}
              className={`${styles.ctrlBtn} ${styles.grip}`}
              aria-label={`Reorder ${section.title}`}
              {...attributes}
              {...listeners}
            >
              <GripIcon />
            </button>
            <button
              type="button"
              className={styles.ctrlBtn}
              aria-haspopup="dialog"
              aria-expanded={popoverId === section.id}
              aria-label={`Who can see ${section.title}`}
              onClick={(e) => onTogglePopover(e, section.id)}
            >
              <SettingsIcon />
            </button>
          </span>
        )}
        {manageable && popoverId === section.id && (
          <AccessPopover
            title={section.title}
            roles={admin.roles}
            checkedIds={section.roleIds ?? []}
            busyKey={admin.busyKey}
            ownerId={section.id}
            everyoneLabel="Everyone signed in"
            anchorRef={popoverAnchorRef}
            footer={
              <RowActions
                kind="section"
                title={section.title}
                busy={admin.mutating}
                deleteHint="Only an empty section can be deleted — move or delete its pages first."
                onRename={(title) => admin.renameSection(section.id, title)}
                onDelete={() => admin.deleteSection(section.id)}
                onDone={() => onClosePopover("escape")}
              />
            }
            onToggle={(roleId) => void admin.toggleSectionRole(section.id, roleId)}
            onClear={() => void admin.clearSectionRoles(section.id)}
            onClose={onClosePopover}
          />
        )}
      </div>
      {open && (
        <PagesList
          section={section}
          manageReady={manageReady}
          admin={admin}
          activePath={activePath}
          popoverId={popoverId}
          popoverAnchorRef={popoverAnchorRef}
          onTogglePopover={onTogglePopover}
          onClosePopover={onClosePopover}
        />
      )}
    </li>
  );
}

interface PagesListProps {
  section: ManagedSection;
  manageReady: boolean;
  admin: ManagedNav;
  activePath: string;
  popoverId: string | null;
  popoverAnchorRef: React.MutableRefObject<HTMLButtonElement | null>;
  onTogglePopover: (e: MouseEvent<HTMLButtonElement>, ownerId: string) => void;
  onClosePopover: (reason: PopoverClose) => void;
}

function PagesList({
  section,
  manageReady,
  admin,
  activePath,
  popoverId,
  popoverAnchorRef,
  onTogglePopover,
  onClosePopover,
}: PagesListProps) {
  const { ensurePages, refreshPages } = admin;
  useEffect(() => {
    if (manageReady) ensurePages(section.id);
  }, [manageReady, section.id, ensurePages]);

  const adminPages = manageReady ? (admin.pagesBySection[section.id] ?? null) : null;
  const pages = useMemo(() => mergePages(section.pages, adminPages), [section.pages, adminPages]);

  // Self-heal a stale admin cache: if nav shows a page the loaded admin list
  // lacks (e.g. one just created here), refetch so it gains its access data.
  useEffect(() => {
    if (!adminPages) return;
    const known = new Set(adminPages.map((p) => p.id));
    if (section.pages.some((p) => !known.has(p.id))) refreshPages(section.id);
  }, [adminPages, section.pages, section.id, refreshPages]);

  const tree = useMemo(() => buildPageTree(pages), [pages]);
  const isActive = (p: ManagedPage) => activePath === `/manual/${section.slug}/${p.slug}`;

  // A parent's children are collapsed unless the reader opens them, or the
  // current page is the parent or one of its children.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const activeParent =
    tree.find((n) => n.children.length > 0 && (isActive(n.page) || n.children.some(isActive)))?.page
      .id ?? null;
  // Arriving at a page opens its parent again, even one the reader closed.
  useEffect(() => {
    if (!activeParent) return;
    setToggled((t) => {
      if (!(activeParent in t)) return t;
      const next = { ...t };
      delete next[activeParent];
      return next;
    });
  }, [activeParent, activePath]);

  const titles = useMemo(() => new Map(pages.map((p) => [p.id, p.title])), [pages]);

  // Reorder stays within one sibling list; the server takes the full order.
  function onMove(activeId: string, overId: string) {
    const ids = moveWithinSiblings(pages, activeId, overId);
    if (ids) void admin.reorderPages(section.id, ids);
  }

  if (pages.length === 0) return null;

  const item = (page: ManagedPage, children: ManagedPage[] = []) => {
    const expanded = children.length > 0 && (toggled[page.id] ?? activeParent === page.id);
    return (
      <PageItem
        key={page.id}
        page={page}
        sectionSlug={section.slug}
        manageReady={manageReady}
        admin={admin}
        sectionId={section.id}
        active={isActive(page)}
        popoverId={popoverId}
        popoverAnchorRef={popoverAnchorRef}
        onTogglePopover={onTogglePopover}
        onClosePopover={onClosePopover}
        childCount={children.length}
        expanded={expanded}
        onToggleChildren={() => setToggled((t) => ({ ...t, [page.id]: !expanded }))}
        childList={
          expanded ? (
            <SortablePages
              ids={children.map((c) => c.id)}
              titles={titles}
              className={`${styles.pages} ${styles.childPages}`}
              onMove={onMove}
            >
              {children.map((c) => item(c))}
            </SortablePages>
          ) : null
        }
      />
    );
  };

  return (
    <SortablePages
      ids={tree.map((n) => n.page.id)}
      titles={titles}
      className={styles.pages ?? ""}
      onMove={onMove}
    >
      {tree.map((n) => item(n.page, n.children))}
    </SortablePages>
  );
}

/** One sibling list of pages: dnd-kit reorders within it only. */
function SortablePages({
  ids,
  titles,
  className,
  onMove,
  children,
}: {
  ids: string[];
  titles: Map<string, string>;
  className: string;
  onMove: (activeId: string, overId: string) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const announcements = useMemo(() => makeAnnouncements(titles), [titles]);

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    onMove(String(active.id), String(over.id));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      accessibility={{ announcements }}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={className}>{children}</ul>
      </SortableContext>
    </DndContext>
  );
}

interface PageItemProps {
  page: ManagedPage;
  sectionSlug: string;
  sectionId: string;
  manageReady: boolean;
  admin: ManagedNav;
  active: boolean;
  popoverId: string | null;
  popoverAnchorRef: React.MutableRefObject<HTMLButtonElement | null>;
  onTogglePopover: (e: MouseEvent<HTMLButtonElement>, ownerId: string) => void;
  onClosePopover: (reason: PopoverClose) => void;
  /** Child pages: a parent gets a disclosure button, its children below it. */
  childCount: number;
  expanded: boolean;
  onToggleChildren: () => void;
  childList: ReactNode;
}

function PageItem({
  page,
  sectionSlug,
  sectionId,
  manageReady,
  admin,
  active,
  popoverId,
  popoverAnchorRef,
  onTogglePopover,
  onClosePopover,
  childCount,
  expanded,
  onToggleChildren,
  childList,
}: PageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.id,
    disabled: !manageReady || page.roleIds === null || admin.reordering,
  });
  const manageable = manageReady && page.roleIds !== null;

  const rowClass = [styles.pageRow, active ? styles.pageActive : ""].filter(Boolean).join(" ");
  const childListId = `pages-in-${page.id}`;

  const label = (
    <>
      {page.title}
      {page.published === false && <span className={styles.draftTag}>draft</span>}
    </>
  );

  return (
    <li
      ref={setNodeRef}
      className={isDragging ? styles.dragging : undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className={rowClass}>
        {childCount > 0 && (
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={expanded}
            aria-controls={expanded ? childListId : undefined}
            aria-label={`Pages in ${page.title}`}
            onClick={onToggleChildren}
          >
            <span aria-hidden="true">▸</span>
          </button>
        )}
        {page.readable ? (
          <Link to={`/manual/${sectionSlug}/${page.slug}`} className={styles.pageLink}>
            {label}
          </Link>
        ) : (
          <span
            className={`${styles.pageLink} ${styles.rowDimmed}`}
            title="Your roles can't see this page — you can still manage it"
          >
            {label}
          </span>
        )}
        {manageable && (
          <span className={styles.rowControls}>
            <button
              type="button"
              ref={setActivatorNodeRef}
              className={`${styles.ctrlBtn} ${styles.grip}`}
              aria-label={`Reorder ${page.title}`}
              {...attributes}
              {...listeners}
            >
              <GripIcon />
            </button>
            <button
              type="button"
              className={styles.ctrlBtn}
              aria-haspopup="dialog"
              aria-expanded={popoverId === page.id}
              aria-label={`Who can see ${page.title}`}
              onClick={(e) => onTogglePopover(e, page.id)}
            >
              <SettingsIcon />
            </button>
          </span>
        )}
        {manageable && popoverId === page.id && (
          <AccessPopover
            title={page.title}
            roles={admin.roles}
            checkedIds={page.roleIds ?? []}
            busyKey={admin.busyKey}
            ownerId={page.id}
            everyoneLabel="Everyone who can see this section"
            anchorRef={popoverAnchorRef}
            footer={
              <RowActions
                kind="page"
                title={page.title}
                busy={admin.mutating}
                deleteHint="Staff stop seeing this page straight away. Its version history stays in the archive."
                onDelete={() => admin.deletePage(sectionId, page.id)}
                onDone={() => onClosePopover("escape")}
              />
            }
            onToggle={(roleId) => void admin.togglePageRole(sectionId, page.id, roleId)}
            onClear={() => void admin.clearPageRoles(sectionId, page.id)}
            onClose={onClosePopover}
          />
        )}
      </div>
      {childList && <div id={childListId}>{childList}</div>}
    </li>
  );
}

function makeAnnouncements(titles: Map<string, string>): Announcements {
  const name = (id: unknown) => titles.get(String(id)) ?? "item";
  return {
    onDragStart: ({ active }) => `Picked up ${name(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${name(active.id)} is now over ${name(over.id)}.` : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${name(active.id)} dropped at the position of ${name(over.id)}.`
        : `${name(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Dragging ${name(active.id)} was cancelled.`,
  };
}
